import { HttpService } from "@app/core/services/http/http.service";
import { Injectable, inject } from "@angular/core";
import { Observable, map } from "rxjs";
import { IPLUS_URL } from "@app/shared/constants/url.constants";
import {
  RoleAdapter,
  RolesAdapter,
  RoleList,
  Role,
  RolesFilteredListAdapter,
  RoleByMenuItemsListAdapter,
  RoleByMenuItemsList
} from "@admin/roles/roles.model";
import {
  ComponentOverridesSettings,
  ComponentOverridesSettingsAdapter
} from "@admin/application-settings/component-overrides/component-overrides.model";

@Injectable({
  providedIn: "root"
})
export class RolesService {
  private http = inject(HttpService);

  /**
   * Gets all of the current roles
   */
  fetchRoles(): Observable<RoleList[]> {
    const rolesAdapter = new RolesAdapter();
    return this.http.get<RoleList>(IPLUS_URL.ROLES_LIST).pipe(map((response: any) => rolesAdapter.adapt(response)));
  }

  /**
   * gets a specific role by its id
   * @param params : id
   */
  fetchRoleById(params: Role) {
    const roleAdapter = new RoleAdapter();
    return this.http
      .addDataParams(params)
      .get<Role>(IPLUS_URL.ROLES_MAINTENANCE)
      .pipe(map((response: any) => roleAdapter.adapt(response)));
  }

  /**
   * Add a role
   * @param data : payload to send back to the server
   */
  addRole(data: Role) {
    const roleAdapter = new RoleAdapter();
    return this.http
      .addDataParams(data)
      .post(IPLUS_URL.ROLES_MAINTENANCE, "")
      .pipe(map((response: any) => roleAdapter.adapt(response)));
  }

  /**
   * Update an existing role
   * @param data : payload to send back to the server
   */
  updateRole(data: Role) {
    const roleAdapter = new RoleAdapter();
    return this.http
      .addDataParams(data)
      .put(IPLUS_URL.ROLES_MAINTENANCE, "")
      .pipe(map((response: any) => roleAdapter.adapt(response)));
  }

  /**
   * Delete a role
   * @param data
   */
  deleteRole(data: Role) {
    const roleAdapter = new RoleAdapter();
    return this.http
      .addDataParams(data)
      .delete(IPLUS_URL.ROLES_DELETE)
      .pipe(map((response: any) => roleAdapter.adapt(response)));
  }

  /**
   * Gets all of the current roles by filter
   */
  fetchRolesByFilter(): Observable<RoleList[]> {
    const rolesAdapter = new RolesFilteredListAdapter();
    return this.http.get<RoleList>(IPLUS_URL.ROLES_FILTERED_LIST).pipe(map((response: any) => rolesAdapter.adapt(response)));
  }

  fetchComponentSettingsById(data: ComponentOverridesSettings) {
    const respAdapter = new ComponentOverridesSettingsAdapter();
    return this.http
      .addDataParams({ roleId: data?.roleId, menuId: data?.menuId, componentId: data.componentId, content: "" })
      .get<ComponentOverridesSettings>(IPLUS_URL.ROLES_COMPONENT_OVERRIDES_MAINTENANCE)
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  updateComponentSettings(data: ComponentOverridesSettings) {
    const respAdapter = new ComponentOverridesSettingsAdapter();
    return this.http
      .addDataParams({ roleId: data.roleId, menuId: data.menuId, componentId: data.componentId, content: data.content })
      .put<ComponentOverridesSettings>(IPLUS_URL.ROLES_COMPONENT_OVERRIDES_MAINTENANCE, "")
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  deleteComponentSettings(roleId: string, menuId: string) {
    const respAdapter = new ComponentOverridesSettingsAdapter();
    return this.http
      .addDataParams({ roleId, menuId })
      .delete<ComponentOverridesSettings>(IPLUS_URL.ROLES_COMPONENT_OVERRIDES_DELETE)
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  /**
   * Fetch list of roles that are associated to the menuId and componentId
   * @param menuId
   * @param componentId
   * @returns
   */
  fetchRolesByIds(menuId: string, componentId: string) {
    const respAdapter = new RoleByMenuItemsListAdapter();
    return this.http
      .addDataParams({ menuId, componentId })
      .get<RoleByMenuItemsList[]>(IPLUS_URL.ROLES_COMPONENT_OVERRIDES_LIST)
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  /**
   * Fetch list of roles that are associated to a componentId
   * @param componentId
   * @returns
   */
  fetchRolesByComponentId(componentId: string) {
    const respAdapter = new RoleByMenuItemsListAdapter();
    return this.http
      .addDataParams({ componentId })
      .get<RoleByMenuItemsList[]>(IPLUS_URL.ROLES_COMPONENT_OVERRIDES_LIST_BY_COMPONENT)
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }
}
